import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Flask,
  Heartbeat,
  ChartLine,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkle,
  Warning,
  WarningCircle
} from '@phosphor-icons/react';

// Age range for predictions (must match backend)
const AGE_RANGE = { min: 35, max: 58 };

// Hormone validation ranges (must match backend HORMONE_RANGES)
const HORMONE_RANGES = {
  fsh: { min: 1, max: 150, unit: 'mIU/mL', label: 'FSH' },
  lh: { min: 1, max: 100, unit: 'mIU/mL', label: 'LH' },
  estradiol_e2: { min: 5, max: 800, unit: 'pg/mL', label: 'Estradiol (E2)' },
  amh: { min: 0.01, max: 15, unit: 'ng/mL', label: 'AMH' },
  inhibin_b: { min: 5, max: 300, unit: 'pg/mL', label: 'Inhibin B' },
  progesterone: { min: 0.1, max: 30, unit: 'ng/mL', label: 'Progesterone' }
};

// Validate hormone values
const validateHormones = (data) => {
  const errors = [];
  
  for (const [key, range] of Object.entries(HORMONE_RANGES)) {
    const value = data[key];
    if (value !== '' && value !== null && value !== undefined) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        errors.push(`${range.label}: Please enter a valid number`);
      } else if (numValue < 0) {
        errors.push(`${range.label}: Value cannot be negative`);
      } else if (numValue < range.min || numValue > range.max) {
        errors.push(`${range.label}: ${numValue} ${range.unit} is outside the expected range (${range.min}-${range.max} ${range.unit}). Please verify your lab report.`);
      }
    }
  }
  
  return errors;
};

// Validate age
const validateAge = (age) => {
  if (!age) return 'Please enter your age';
  const numAge = parseInt(age);
  if (isNaN(numAge)) return 'Please enter a valid age';
  if (numAge < AGE_RANGE.min) return `Our predictions are designed for women aged ${AGE_RANGE.min} and above. If you're younger, please check back in a few years.`;
  if (numAge > AGE_RANGE.max) return `Our predictions are optimized for women up to age ${AGE_RANGE.max}. You may have already completed your menopause journey.`;
  return null;
};

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkle },
  { id: 'hormones', title: 'Lab Results', icon: Flask },
  { id: 'lifestyle', title: 'Lifestyle', icon: Heartbeat },
  { id: 'complete', title: 'Get Results', icon: ChartLine }
];

export const OnboardingWizard = ({ onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Get user ID from context or try to decode from token
  const getUserId = () => {
    if (user?.user_id) return user.user_id;
    // Try to get from token if user context not ready
    const token = localStorage.getItem('menopath_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub;
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
    return null;
  };
  
  const userId = getUserId();
  
  // Hormone data state
  const [hormoneData, setHormoneData] = useState({
    fsh: '',
    lh: '',
    estradiol_e2: '',
    amh: '',
    inhibin_b: '',
    progesterone: '',
    test_date: new Date().toISOString().split('T')[0]
  });
  
  // Lifestyle data state
  const [lifestyleData, setLifestyleData] = useState({
    age: '',
    bmi: '',
    smoking_status: 'never',
    alcohol_consumption: 'rarely',
    exercise_frequency: 'moderate',
    sleep_hours: '7',
    sleep_quality: '7',
    ethnicity: ''
  });

  const handleHormoneChange = (field, value) => {
    setHormoneData(prev => ({ ...prev, [field]: value }));
    setValidationErrors([]); // Clear errors when user types
  };

  const handleLifestyleChange = (field, value) => {
    setLifestyleData(prev => ({ ...prev, [field]: value }));
    setValidationErrors([]); // Clear errors when user types
  };

  const handleNext = async () => {
    setValidationErrors([]);
    
    if (currentStep === 0 && !disclaimerAccepted) {
      toast.error('Please accept the disclaimer to continue');
      return;
    }
    
    if (currentStep === 1) {
      // Validate hormone data
      const hasAnyHormone = Object.entries(hormoneData)
        .filter(([key]) => key !== 'test_date')
        .some(([_, value]) => value !== '');
      
      if (!hasAnyHormone) {
        toast.error('Please enter at least one hormone value');
        return;
      }
      
      // Validate hormone ranges
      const hormoneErrors = validateHormones(hormoneData);
      if (hormoneErrors.length > 0) {
        setValidationErrors(hormoneErrors);
        return;
      }
      
      setIsSubmitting(true);
      try {
        const dataToSubmit = {
          ...Object.fromEntries(
            Object.entries(hormoneData)
              .filter(([_, v]) => v !== '')
              .map(([k, v]) => [k, k === 'test_date' ? v : parseFloat(v)])
          )
        };
        
        // Verify token exists before making API call
        const token = localStorage.getItem('menopath_token');
        if (!token) {
          toast.error('Session expired. Please log in again.');
          setIsSubmitting(false);
          return;
        }
        
        await api.createHormonalData(dataToSubmit);
        toast.success('Lab results saved!');
        // Mark onboarding as started/in-progress with hormone data saved
        localStorage.setItem(`menopath_onboarding_${userId}`, 'in_progress');
      } catch (error) {
        console.error('Failed to save lab results:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else {
          toast.error('Failed to save lab results');
        }
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    }
    
    if (currentStep === 2) {
      // Validate age
      const ageError = validateAge(lifestyleData.age);
      if (ageError) {
        setValidationErrors([ageError]);
        return;
      }
      
      // Validate BMI if provided
      if (lifestyleData.bmi) {
        const bmi = parseFloat(lifestyleData.bmi);
        if (isNaN(bmi) || bmi < 10 || bmi > 60) {
          setValidationErrors(['BMI should be between 10 and 60. Please verify your calculation.']);
          return;
        }
      }
      
      setIsSubmitting(true);
      try {
        const dataToSubmit = {
          age: parseInt(lifestyleData.age),
          bmi: lifestyleData.bmi ? parseFloat(lifestyleData.bmi) : null,
          smoking_status: lifestyleData.smoking_status,
          alcohol_consumption: lifestyleData.alcohol_consumption,
          exercise_frequency: lifestyleData.exercise_frequency,
          sleep_hours: parseFloat(lifestyleData.sleep_hours),
          sleep_quality: parseInt(lifestyleData.sleep_quality),
          ethnicity: lifestyleData.ethnicity || null
        };
        await api.createLifestyleData(dataToSubmit);
        toast.success('Lifestyle data saved!');
      } catch (error) {
        toast.error('Failed to save lifestyle data');
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setValidationErrors([]);
    }
  };

  const handleComplete = () => {
    // Mark onboarding as complete
    localStorage.setItem(`menopath_onboarding_${userId}`, 'completed');
    onComplete();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep 
          userName={user?.name} 
          accepted={disclaimerAccepted} 
          onAccept={setDisclaimerAccepted} 
        />;
      case 1:
        return <HormoneStep 
          data={hormoneData} 
          onChange={handleHormoneChange} 
          errors={validationErrors}
          ranges={HORMONE_RANGES}
        />;
      case 2:
        return <LifestyleStep 
          data={lifestyleData} 
          onChange={handleLifestyleChange} 
          errors={validationErrors}
          ageRange={AGE_RANGE}
        />;
      case 3:
        return <CompleteStep onGetPrediction={handleComplete} />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Progress Header */}
        <div className="bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold text-lg">Account Setup</h2>
            <span className="text-white/70 text-sm">Step {currentStep + 1} of {STEPS.length}</span>
          </div>
          
          {/* Step Indicators */}
          <div className="flex items-center gap-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-all
                    ${isActive ? 'bg-white text-[#5B21B6]' : 
                      isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/50'}
                  `}>
                    {isCompleted ? (
                      <Check className="w-5 h-5" weight="bold" />
                    ) : (
                      <Icon className="w-5 h-5" weight={isActive ? 'duotone' : 'regular'} />
                    )}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded-full ${
                      isCompleted ? 'bg-white/50' : 'bg-white/10'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Footer */}
        <div className="border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between bg-[#F8FAFC]">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="text-[#64748B]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className="bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] hover:opacity-90"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : null}
              {currentStep === 0 ? 'Get Started' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              className="bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] hover:opacity-90"
            >
              <ChartLine className="w-4 h-4 mr-2" />
              View My Dashboard
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Step Components

const WelcomeStep = ({ userName, accepted, onAccept }) => (
  <div className="text-center">
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#5B21B6] to-[#EC4899] mx-auto mb-6 flex items-center justify-center">
      <Sparkle className="w-10 h-10 text-white" weight="duotone" />
    </div>
    
    <h3 className="text-2xl font-bold text-[#0F172A] mb-2">
      Welcome to MenoPath{userName ? `, ${userName.split(' ')[0]}` : ''}!
    </h3>
    <p className="text-[#64748B] mb-6">
      Let's set up your profile to generate your personalized menopause prediction.
    </p>

    <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6 text-left">
      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
        <ChartLine className="w-5 h-5 text-[#5B21B6]" />
        Here's what we'll do:
      </h4>
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#5B21B6] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
          <div>
            <p className="font-medium text-[#0F172A]">Enter Your Lab Results</p>
            <p className="text-sm text-[#64748B]">Your hormone levels (FSH, AMH, Estradiol, etc.)</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
          <div>
            <p className="font-medium text-[#0F172A]">Share Lifestyle Factors</p>
            <p className="text-sm text-[#64748B]">Age, sleep, exercise, and other health factors</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#EC4899] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
          <div>
            <p className="font-medium text-[#0F172A]">Get Your Prediction</p>
            <p className="text-sm text-[#64748B]">AI-powered menopause timeline and wellness insights</p>
          </div>
        </div>
      </div>
    </div>

    {/* Disclaimer */}
    <div className="bg-[#FEF3C7] rounded-xl p-4 border border-[#FDE68A] text-left mb-4">
      <div className="flex items-start gap-3">
        <Warning className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-[#92400E] mb-1">Important Disclaimer</h4>
          <p className="text-sm text-[#92400E] leading-relaxed">
            MenoPath is for <strong>informational purposes only</strong> and is not intended to diagnose, 
            treat, or predict any medical condition. Results are estimates based on statistical models 
            and may not be accurate for every individual. <strong>Always consult a qualified healthcare 
            professional</strong> for medical decisions.
          </p>
        </div>
      </div>
    </div>

    <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-[#E2E8F0] hover:border-[#5B21B6] cursor-pointer transition-colors">
      <input 
        type="checkbox" 
        checked={accepted} 
        onChange={(e) => onAccept(e.target.checked)}
        className="w-5 h-5 rounded border-[#CBD5E1] text-[#5B21B6] focus:ring-[#5B21B6]"
      />
      <span className="text-sm text-[#334155]">
        I understand and accept this disclaimer
      </span>
    </label>
  </div>
);

const HormoneStep = ({ data, onChange, errors, ranges }) => {
  const hormoneFields = [
    { key: 'fsh', label: 'FSH', unit: 'mIU/mL', hint: 'Follicle Stimulating Hormone' },
    { key: 'lh', label: 'LH', unit: 'mIU/mL', hint: 'Luteinizing Hormone' },
    { key: 'estradiol_e2', label: 'Estradiol (E2)', unit: 'pg/mL', hint: 'Primary estrogen' },
    { key: 'amh', label: 'AMH', unit: 'ng/mL', hint: 'Anti-Müllerian Hormone' },
    { key: 'inhibin_b', label: 'Inhibin B', unit: 'pg/mL', hint: 'Ovarian reserve marker' },
    { key: 'progesterone', label: 'Progesterone', unit: 'ng/mL', hint: 'Luteal phase hormone' }
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-[#5B21B6]/10 flex items-center justify-center">
          <Flask className="w-6 h-6 text-[#5B21B6]" weight="duotone" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#0F172A]">Enter Your Lab Results</h3>
          <p className="text-sm text-[#64748B]">Add values from your recent hormone test</p>
        </div>
      </div>

      <div className="bg-[#F0FDF4] rounded-lg p-3 mb-4 border border-[#BBF7D0]">
        <p className="text-sm text-[#166534]">
          <strong>Tip:</strong> Enter at least FSH and AMH for the most accurate prediction. 
          Leave unknown values blank.
        </p>
      </div>

      {/* Validation Errors */}
      {errors && errors.length > 0 && (
        <div className="bg-[#FEF2F2] rounded-lg p-3 mb-4 border border-[#FECACA]">
          <div className="flex items-start gap-2">
            <WarningCircle className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#991B1B] mb-1">Please fix the following:</p>
              <ul className="text-sm text-[#DC2626] space-y-1">
                {errors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium text-[#334155] mb-1">Test Date</label>
        <input
          type="date"
          value={data.test_date}
          onChange={(e) => onChange('test_date', e.target.value)}
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {hormoneFields.map((field) => {
          const range = ranges[field.key];
          return (
            <div key={field.key}>
              <label className="block text-sm font-medium text-[#334155] mb-1">
                {field.label}
                <span className="text-[#94A3B8] font-normal ml-1">({field.unit})</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder={field.hint}
                value={data[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent text-sm"
              />
              <p className="text-xs text-[#94A3B8] mt-1">Range: {range.min}-{range.max} {range.unit}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LifestyleStep = ({ data, onChange, errors, ageRange }) => (
  <div>
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 rounded-full bg-[#7C3AED]/10 flex items-center justify-center">
        <Heartbeat className="w-6 h-6 text-[#7C3AED]" weight="duotone" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-[#0F172A]">Lifestyle Factors</h3>
        <p className="text-sm text-[#64748B]">These help personalize your prediction</p>
      </div>
    </div>

    {/* Validation Errors */}
    {errors && errors.length > 0 && (
      <div className="bg-[#FEF2F2] rounded-lg p-3 mb-4 border border-[#FECACA]">
        <div className="flex items-start gap-2">
          <WarningCircle className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#991B1B] mb-1">Please fix the following:</p>
            <ul className="text-sm text-[#DC2626] space-y-1">
              {errors.map((error, idx) => (
                <li key={idx}>• {error}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )}

    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">
            Age <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={ageRange.min}
            max={ageRange.max}
            placeholder={`${ageRange.min}-${ageRange.max}`}
            value={data.age}
            onChange={(e) => onChange('age', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
          />
          <p className="text-xs text-[#94A3B8] mt-1">Predictions available for ages {ageRange.min}-{ageRange.max}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">BMI</label>
          <input
            type="number"
            step="0.1"
            min="10"
            max="60"
            placeholder="e.g., 24.5"
            value={data.bmi}
            onChange={(e) => onChange('bmi', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
          />
          <p className="text-xs text-[#94A3B8] mt-1">Optional (10-60)</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#334155] mb-1">Ethnicity</label>
        <select
          value={data.ethnicity}
          onChange={(e) => onChange('ethnicity', e.target.value)}
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
        >
          <option value="">Select ethnicity</option>
          <option value="caucasian">Caucasian / White</option>
          <option value="african_american">African American / Black</option>
          <option value="hispanic">Hispanic / Latina</option>
          <option value="asian">Asian</option>
          <option value="middle_eastern">Middle Eastern</option>
          <option value="mixed">Mixed / Other</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">Smoking Status</label>
          <select
            value={data.smoking_status}
            onChange={(e) => onChange('smoking_status', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
          >
            <option value="never">Never smoked</option>
            <option value="former">Former smoker</option>
            <option value="current">Current smoker</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">Alcohol Consumption</label>
          <select
            value={data.alcohol_consumption}
            onChange={(e) => onChange('alcohol_consumption', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
          >
            <option value="never">Never</option>
            <option value="rarely">Rarely</option>
            <option value="moderate">Moderate</option>
            <option value="heavy">Heavy</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#334155] mb-1">Exercise Frequency</label>
        <select
          value={data.exercise_frequency}
          onChange={(e) => onChange('exercise_frequency', e.target.value)}
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
        >
          <option value="never">Never</option>
          <option value="rarely">Rarely (1-2x/month)</option>
          <option value="moderate">Moderate (1-2x/week)</option>
          <option value="frequent">Frequent (3-4x/week)</option>
          <option value="daily">Daily</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">Sleep Hours</label>
          <input
            type="number"
            step="0.5"
            min="1"
            max="16"
            placeholder="e.g., 7"
            value={data.sleep_hours}
            onChange={(e) => onChange('sleep_hours', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-1">Sleep Quality (1-10)</label>
          <input
            type="range"
            min="1"
            max="10"
            value={data.sleep_quality}
            onChange={(e) => onChange('sleep_quality', e.target.value)}
            className="w-full mt-2"
          />
          <div className="flex justify-between text-xs text-[#94A3B8]">
            <span>Poor</span>
            <span className="font-medium text-[#5B21B6]">{data.sleep_quality}</span>
            <span>Excellent</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CompleteStep = ({ onGetPrediction }) => (
  <div className="text-center py-6">
    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] mx-auto mb-6 flex items-center justify-center">
      <Check className="w-12 h-12 text-white" weight="bold" />
    </div>
    
    <h3 className="text-2xl font-bold text-[#0F172A] mb-2">You're All Set!</h3>
    <p className="text-[#64748B] mb-8 max-w-md mx-auto">
      Your profile is complete. Head to your dashboard to run your first AI-powered 
      menopause prediction and explore your personalized wellness insights.
    </p>

    <div className="bg-gradient-to-r from-[#5B21B6]/5 to-[#EC4899]/5 rounded-xl p-6 border border-[#E2E8F0]">
      <h4 className="font-semibold text-[#0F172A] mb-4">What's next?</h4>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#5B21B6]/10 mx-auto mb-2 flex items-center justify-center">
            <ChartLine className="w-5 h-5 text-[#5B21B6]" />
          </div>
          <p className="text-[#64748B]">Run AI Analysis</p>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#EC4899]/10 mx-auto mb-2 flex items-center justify-center">
            <Sparkle className="w-5 h-5 text-[#EC4899]" />
          </div>
          <p className="text-[#64748B]">View Prediction</p>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#10B981]/10 mx-auto mb-2 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#10B981]" />
          </div>
          <p className="text-[#64748B]">Track Wellness</p>
        </div>
      </div>
    </div>
  </div>
);

export default OnboardingWizard;
